import React, { Fragment } from 'react';
import { useIsMobile } from 'hooks/use-breakpoint-checks.hook';
import PageTitle from 'components/layout/PageTitle';
import Footer from 'components/layout/Footer';
import { usePublisherAnalytics } from 'features/PublisherAnalytics/hooks';
import { ANALYTICS_TIMEFRAME } from 'features/PublisherAnalytics/models/publishers.model';
import {
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Skeleton
} from '@mui/material';
import { fillParent, scrollingBox, footerFlex } from 'utils/base-styles';
import { formatLargeNumber, formatNumber } from 'features/PublisherAnalytics/utils/util';
import TopArticlesDataGrid from 'features/PublisherAnalytics/components/TopArticlesTable';
import TopicPercentageChart from 'features/PublisherAnalytics/components/TopicPercentageChart';
import KeySearchTermsCard from 'features/PublisherAnalytics/components/KeySearchTerm';
import GradeLevelDemographics from 'features/PublisherAnalytics/components/GradeLevelDemographics';
import EngagementByHourChart from './EngagementLineChart';
import EngagementBox from 'features/PublisherAnalytics/components/EngagementBox';
import TimeFrameSelect from 'features/PublisherAnalytics/components/TimeFrameSelect';
import PublisherSelect from 'features/PublisherAnalytics/components/PublisherSelect';
import { Helmet } from 'react-helmet-async';

export default function Engagement() {
  const isMobile = useIsMobile();
  const { timeframe, setTimeframe,periodTimeFrame, setPeriodTimeFrame,
    publisherId, setPublisherId,
    publisherEngagement, topArticles, topicPercentage, 
    searchTerms, demographics, averageClicks,
    isLoadingEngagement, isLoadingTopArticles, isLoadingTopicPercentage,
    isLoadingSearchTerms, isLoadingDemographics, isLoadingAverageClicks, clickThroughRate } = usePublisherAnalytics();
  

  const stats = publisherEngagement && clickThroughRate ? [
     {
      label: 'Total Visits',
      raw: publisherEngagement.totalVisits || 0,
      change: publisherEngagement.totalVisitsPercChangeInt,
      format: formatLargeNumber
    },
    {
      label: 'Unique Visitors',
      raw: publisherEngagement.uniqueVisitors || 0,
      change: publisherEngagement.uniqueVisitorsPercChangeInt,
      format: formatLargeNumber
    },
    {
      label: 'Total Impressions',
      raw: clickThroughRate.searchAppearances || 0,
      change: clickThroughRate.searchAppearancesChangePercentInt,
      format: formatLargeNumber
    },
    {
      label: 'Overall Rank',
      raw: publisherEngagement.overallRank ?? null,
      change: publisherEngagement.overallRankChange,
      format: (v) => v === null || v === undefined ? '-' : formatNumber(v)
    },
    {
      label: 'Click Through Rate',
      raw: clickThroughRate.clickThroughRateInt || 0,
      change: clickThroughRate.clickThroughRateChangePercentInt,
      format: (v) => `${v}%`
    },
    {
      label: 'First Click',
      raw: publisherEngagement.firstClickPercInt || 0,
      change: publisherEngagement.firstClickPercChangeInt,
      format: (v) => `${v}%`
    },
    {
      label: 'New Readers',
      raw: publisherEngagement.newReaders || 0,
      change: publisherEngagement.newReadersPercChangeInt,
      format: formatLargeNumber
    },
    {
      label: 'Retention',
      raw: publisherEngagement.retentionPercInt || 0,
      change: publisherEngagement.retentionPercChangeInt,
      format: (v) => `${v}%`
    },
    {
      label: 'New Bookmarks',
      raw: publisherEngagement.newBookmarks || 0,
      change: publisherEngagement.newBookmarksPercChangeInt,
      format: formatLargeNumber
    }
  ].map(stat => ({
    ...stat,
    change: stat.change === null ? null : stat.change,
    hasChange: stat.change !== null
  })) : [];

  return (
    <Fragment>
       <Helmet>
         <title>Sooth - Publishers - Engagement</title>
       </Helmet>
      <Box sx={{
        "@media screen": {
          ...fillParent,
          ...scrollingBox,
          ...footerFlex,
        },
        "@media print": {
          m: 3,
        },
      }}>
        <Box sx={{ display: 'grid', gap: 2, mb: 7, p: 2 }}>
          <Stack
          sx={{
            flexDirection: { md: 'column', lg: 'row' }
          }}
            justifyContent="space-between"
            alignItems="center"
          >
            <PageTitle sx={{ mt: 2, mb: 2, display: "flex", flexWrap: "wrap" }}>Your Engagement with Readers</PageTitle>
            <Stack direction={{ sm: 'column', md: 'row' }} spacing={2} alignItems="center">
              <PublisherSelect />
              <TimeFrameSelect timeframe={timeframe} setTimeframe={setTimeframe} label="Timeframe" />
            </Stack>
          </Stack>

          {isLoadingEngagement ? (
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
          ) : (
            <EngagementBox stats={stats} />
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              gridTemplateColumns: {
                xs: '1fr',
                md: '1fr 1fr',
                lg: '35% 35% 1fr'
              }
            }}
          >
            <Box sx={{ gridColumn: { md: '1 / -1', lg: 'auto' } }}>
              <TopArticlesDataGrid articles={topArticles} isLoading={isLoadingTopArticles} />
            </Box>
            {isLoadingTopicPercentage ? (
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
            ) : (
              <TopicPercentageChart topicPercentage={topicPercentage} />
            )}
            {isLoadingSearchTerms ? (
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
            ) : (
              <KeySearchTermsCard searchTerms={searchTerms} />
            )}
          </Box>
          {/* <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <TimeFrameSelect timeframe={periodTimeFrame} setTimeframe={setPeriodTimeFrame} label="Period Timeframe" />
            </Box> */}
           <Box sx={{ display: "grid", 
            mt: 2,
              gridTemplateColumns: {
                xs: '1fr',
                lg: '35% 1fr'
              }, 
              gap: 2 }}>
            {isLoadingDemographics ? (
              <Skeleton variant="circular" width={300} height={300} />
            ) : (
              <GradeLevelDemographics demographics={demographics} />
            )}
            {isLoadingAverageClicks ? (
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
            ) : (
              <EngagementByHourChart data={averageClicks} />
            )}
            </Box>
        </Box>
        <Footer sx={{ px: 0 }} />
      </Box>
    </Fragment>
  );
}
